import { useRef, useLayoutEffect } from 'react';
import { getOrCreateKeyframes } from './generateCollapseKeyframes';

const COLLAPSE_DURATION_MS = 350;
const EXPAND_DURATION_MS = 350; // longer to let the spring settle
const SAFETY_TIMEOUT_MS = 600;

/**
 * Orchestrates compositor-friendly collapse/expand animations for campaign cards.
 *
 * Instead of animating `height` (which forces layout recalculation every frame),
 * this hook uses the FLIP technique with `transform: scaleY()`:
 *
 * 1. React applies the new CSS class (collapsed/expanded) — card snaps to target height
 * 2. In useLayoutEffect (before paint), we measure both heights
 * 3. Apply scaleY(fromHeight/toHeight) so the card VISUALLY starts at the old height
 * 4. Animate scaleY → 1 via pre-generated @keyframes (runs on the compositor)
 * 5. Counter-scale direct children with inverse scaleY so content isn't squished
 * 6. On animationend, remove all inline styles — card is at its real CSS height
 *
 * Cards marked data-offscreen by the IntersectionObserver skip all animation
 * work — their CSS transitions are already disabled so they snap instantly.
 */
export function useCollapseAnimation(isCollapsed: boolean, opts?: { disabled?: boolean }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const prevCollapsedRef = useRef(isCollapsed);
  const cleanupTimerRef = useRef<NodeJS.Timeout>(undefined);
  const collapsingTimerRef = useRef<NodeJS.Timeout>(undefined);
  const restingHeightRef = useRef<number>(0);
  const animationEndHandlerRef = useRef<((e: AnimationEvent) => void) | null>(null);

  // Capture initial resting height on mount
  useLayoutEffect(() => {
    if (cardRef.current && restingHeightRef.current === 0) {
      restingHeightRef.current = cardRef.current.getBoundingClientRect().height;
    }
  }, []);

  useLayoutEffect(() => {
    if (prevCollapsedRef.current === isCollapsed) return;
    prevCollapsedRef.current = isCollapsed;

    const card = cardRef.current;
    if (!card) return;

    // When disabled, skip FLIP animation — card uses CSS height transition instead.
    if (opts?.disabled) {
      restingHeightRef.current = card.getBoundingClientRect().height;
      window.dispatchEvent(new CustomEvent('force-text-scale'));
      return;
    }

    // Skip animation work for off-screen cards — their CSS transitions
    // are disabled so height/opacity snap instantly, no GPU promotion needed.
    const carouselItem = card.closest('[data-offscreen]');
    if (carouselItem?.getAttribute('data-offscreen') === 'true') {
      restingHeightRef.current = card.getBoundingClientRect().height;
      return;
    }

    // --- MEASURE (before browser paints) ---
    // React already applied the new CSS class, so getBCR gives the TARGET height.
    // restingHeightRef has the height from before this render.
    const fromHeight = restingHeightRef.current;
    const toHeight = card.getBoundingClientRect().height;

    if (!fromHeight || !toHeight || fromHeight === toHeight) {
      restingHeightRef.current = toHeight || fromHeight;
      return;
    }

    // --- GENERATE / CACHE KEYFRAMES ---
    const animName = getOrCreateKeyframes(fromHeight, toHeight);
    const isExpanding = toHeight > fromHeight;
    const duration = isExpanding ? EXPAND_DURATION_MS : COLLAPSE_DURATION_MS;

    // --- TEARDOWN PREVIOUS ANIMATION (if still running) ---
    // Remove any stale animationend listener from a prior rapid toggle
    // to prevent double-cleanup with stale children references.
    if (animationEndHandlerRef.current) {
      card.removeEventListener('animationend', animationEndHandlerRef.current);
      animationEndHandlerRef.current = null;
    }
    clearTimeout(cleanupTimerRef.current);
    clearTimeout(collapsingTimerRef.current);

    // --- APPLY ---
    card.setAttribute('data-animating', 'true');
    if (!isExpanding) {
      card.setAttribute('data-collapsing', 'true');
      // Remove masking early — after the squeeze phase (15% of duration),
      // let the background image start fading back in while folding continues.
      collapsingTimerRef.current = setTimeout(() => {
        card.removeAttribute('data-collapsing');
      }, Math.round(duration * 0.2));
    }
    card.style.willChange = 'transform';
    card.style.transformOrigin = 'top center';
    card.style.animation = `${animName}-parent ${duration}ms linear both`;

    // Counter-scale all direct children so they don't get visually squished
    const children = Array.from(card.children) as HTMLElement[];
    for (const child of children) {
      child.style.transformOrigin = 'top center';
      child.style.animation = `${animName}-child ${duration}ms linear both`;
    }

    // --- CLEANUP ---
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;

      card.style.animation = '';
      card.style.transformOrigin = '';
      card.style.willChange = '';
      card.removeAttribute('data-animating');
      card.removeAttribute('data-collapsing');
      clearTimeout(collapsingTimerRef.current);

      for (const child of children) {
        child.style.animation = '';
        child.style.transformOrigin = '';
      }

      card.removeEventListener('animationend', handleAnimationEnd);
      animationEndHandlerRef.current = null;
      clearTimeout(cleanupTimerRef.current);

      // Cache the new resting height for the next animation
      restingHeightRef.current = card.getBoundingClientRect().height;

      // Tell text scaling hooks to recalculate now that layout is final
      window.dispatchEvent(new CustomEvent('force-text-scale'));
    };

    const handleAnimationEnd = (e: AnimationEvent) => {
      if (e.target === card) {
        cleanup();
      }
    };

    animationEndHandlerRef.current = handleAnimationEnd;
    card.addEventListener('animationend', handleAnimationEnd);

    // Safety fallback if animationend doesn't fire
    cleanupTimerRef.current = setTimeout(cleanup, SAFETY_TIMEOUT_MS);
  }, [isCollapsed]);

  useLayoutEffect(() => {
    return () => {
      clearTimeout(cleanupTimerRef.current);
      clearTimeout(collapsingTimerRef.current);
    };
  }, []);

  return cardRef;
}
